/**
 * Research Section - The Profitability Paradox Explained
 *
 * Clean layout with data-backed insights
 * Background: Light section with subtle gradient
 */

import { SectionHeading, Muted } from "@/components/ui/section-heading";

const ResearchSection = () => {
  return (
    <section className="relative bg-white/90 py-16 sm:py-24 lg:py-32 overflow-hidden shadow-2xl rounded-3xl">
      {/* CONTENT CONTAINER */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* MAIN HEADLINE - Centered */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <SectionHeading variant="question" className="mb-4 sm:mb-6">
            What is The Profitability Paradox?
          </SectionHeading>
          <SectionHeading variant="headline">
            When revenue grows…
            <br />
            <Muted>but margins don't.</Muted>
          </SectionHeading>
        </div>

        {/* THE PARADOX: Two-column layout with text and graph */}
        <div className="bg-white/90 rounded-2xl p-4 py-5 sm:p-8 sm:py-12 lg:p-12 lg:py-16 shadow-2xl mb-10 sm:mb-16 lg:mb-20 border-2 border-slate-200">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            {/* LEFT: Text Content */}
            <div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 leading-tight mb-4 sm:mb-6">
                Preparing and being proactive are zero-risk actions
              </h3>
              <p className="text-xl sm:text-2xl text-slate-600 mb-4 leading-relaxed">
                The World Economic Forum modeled four possible futures for
                business through 2030.{" "}
                <strong>
                  In every single one, the companies that invested early in
                  operational readiness captured the margin gains.
                </strong>{" "}
                There was no scenario where waiting paid off.
                <sup className="text-[#B87333] font-semibold ml-1">
                  <a href="#citation-4" className="hover:underline">
                    [4]
                  </a>
                </sup>
              </p>
            </div>

            {/* RIGHT: Graph */}
            <div>
              <div className="relative w-full">
                <style>{`
                  @keyframes drawLine {
                    to {
                      stroke-dashoffset: 0;
                    }
                  }

                  .revenue-line {
                    stroke-dasharray: 500;
                    stroke-dashoffset: 500;
                    animation: drawLine 2s ease-out forwards;
                  }

                  .margin-line {
                    stroke-dasharray: 500;
                    stroke-dashoffset: 500;
                    animation: drawLine 2s ease-out 0.3s forwards;
                  }
                `}</style>

                <svg
                  viewBox="0 0 400 200"
                  className="w-full"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Grid lines */}
                  <g className="grid-lines" opacity="0.15">
                    <line
                      x1="40"
                      y1="20"
                      x2="380"
                      y2="20"
                      stroke="#64748b"
                      strokeWidth="1"
                    />
                    <line
                      x1="40"
                      y1="60"
                      x2="380"
                      y2="60"
                      stroke="#64748b"
                      strokeWidth="1"
                    />
                    <line
                      x1="40"
                      y1="100"
                      x2="380"
                      y2="100"
                      stroke="#64748b"
                      strokeWidth="1"
                    />
                    <line
                      x1="40"
                      y1="140"
                      x2="380"
                      y2="140"
                      stroke="#64748b"
                      strokeWidth="1"
                    />
                    <line
                      x1="40"
                      y1="180"
                      x2="380"
                      y2="180"
                      stroke="#64748b"
                      strokeWidth="1"
                    />
                  </g>

                  {/* Axes */}
                  <line
                    x1="40"
                    y1="180"
                    x2="380"
                    y2="180"
                    stroke="#475569"
                    strokeWidth="2"
                  />
                  <line
                    x1="40"
                    y1="20"
                    x2="40"
                    y2="180"
                    stroke="#475569"
                    strokeWidth="2"
                  />

                  {/* Revenue Line (ascending) - Copper */}
                  <defs>
                    <linearGradient
                      id="revenueGrad"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#B87333" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#B87333" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Revenue fill area */}
                  <path
                    d="M 60 160 L 120 140 L 180 110 L 240 75 L 300 35 L 360 20 L 360 180 L 60 180 Z"
                    fill="url(#revenueGrad)"
                  />

                  {/* Revenue line with animation */}
                  <path
                    id="revenuePath"
                    className="revenue-line"
                    d="M 60 160 L 120 140 L 180 110 L 240 75 L 300 35 L 360 20"
                    fill="none"
                    stroke="#B87333"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Animated dot on revenue line */}
                  <circle r="4" fill="#B87333">
                    <animateMotion
                      dur="3s"
                      repeatCount="indefinite"
                      path="M 60 160 L 120 140 L 180 110 L 240 75 L 300 35 L 360 20"
                    />
                  </circle>

                  {/* Margin Line (flat/declining) - Red */}
                  <defs>
                    <linearGradient
                      id="marginGrad"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        stopColor="#EF4444"
                        stopOpacity="0.15"
                      />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Margin fill area */}
                  <path
                    d="M 60 140 L 120 142 L 180 145 L 240 148 L 300 152 L 360 155 L 360 180 L 60 180 Z"
                    fill="url(#marginGrad)"
                  />

                  {/* Margin line with animation */}
                  <path
                    id="marginPath"
                    className="margin-line"
                    d="M 60 140 L 120 142 L 180 145 L 240 148 L 300 152 L 360 155"
                    fill="none"
                    stroke="#EF4444"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Animated dot on margin line */}
                  <circle r="4" fill="#EF4444">
                    <animateMotion
                      dur="3s"
                      repeatCount="indefinite"
                      path="M 60 140 L 120 142 L 180 145 L 240 148 L 300 152 L 360 155"
                    />
                  </circle>

                  {/* Y-axis labels */}
                  <text
                    x="36"
                    y="25"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                    textAnchor="end"
                  >
                    High
                  </text>
                  <text
                    x="36"
                    y="176"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                    textAnchor="end"
                  >
                    Low
                  </text>

                  {/* X-axis labels */}
                  <text
                    x="55"
                    y="195"
                    fill="#64748b"
                    fontSize="11"
                    fontWeight="500"
                  >
                    Year 1
                  </text>
                  <text
                    x="170"
                    y="195"
                    fill="#64748b"
                    fontSize="11"
                    fontWeight="500"
                  >
                    Year 3
                  </text>
                  <text
                    x="345"
                    y="195"
                    fill="#64748b"
                    fontSize="11"
                    fontWeight="500"
                  >
                    Year 5
                  </text>
                </svg>

                {/* Legend */}
                <div className="flex items-center justify-center gap-4 sm:gap-8 mt-3 sm:mt-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-6 sm:w-10 h-1 bg-[#B87333] rounded" />
                    <span className="text-xs sm:text-base text-slate-700 font-semibold">
                      Revenue Growth
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-6 sm:w-10 h-1 bg-[#EF4444] rounded" />
                    <span className="text-xs sm:text-base text-slate-700 font-semibold">
                      Profit Margins
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* WHY IT HAPPENS: Four consolidated cards with enhanced style */}
        <div className="mb-8 sm:mb-12 lg:mb-16">
          <SectionHeading
            variant="question"
            className="mb-2 sm:mb-3 text-center"
          >
            How does this happen?
          </SectionHeading>
          <SectionHeading
            variant="subheadline"
            className="mb-8 sm:mb-12 lg:mb-16 text-center"
          >
            It's a natural consequence of growth
            <br />
            <Muted>without sufficient systems.</Muted>
          </SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* CARD 1: Operational Friction & Fragmented Systems */}
            <div className="bg-white/90 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-slate-200 flex flex-col">
              {/* Text Section - White background at top */}
              <div className="bg-white/90 p-5 pb-4 sm:p-8 sm:pb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-[#B87333]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                    Operational Friction & Fragmented Systems
                  </h3>
                </div>
                <p className="text-slate-600 font-semibold leading-relaxed mb-4">
                  Manual processes drain resources—every order, invoice, and
                  report consumes staff hours. Teams log into{" "}
                  <strong className="text-[#B87333]">
                    5-6 different systems daily
                  </strong>
                  , manually updating data, chasing inventory numbers, and
                  merging reports instead of driving growth.
                  <sup className="text-[#B87333] font-semibold ml-1">
                    <a href="#citation-2" className="hover:underline">
                      [2]
                    </a>
                  </sup>
                </p>
                <a
                  href="/case-studies"
                  className="inline-flex items-center text-[#B87333] font-semibold text-sm hover:gap-2 transition-all duration-200"
                >
                  See how we've solved for this
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>

              {/* Image Section Below */}
              <div className="relative h-64 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                <img
                  src="/operation_friction.png"
                  alt="Operational friction and fragmented systems - multiple devices connected to central system"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* CARD 2: Hidden Costs & Rising Expenses */}
            <div className="bg-white/90 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-slate-200 flex flex-col">
              {/* Text Section - White background at top */}
              <div className="bg-white/90 p-5 pb-4 sm:p-8 sm:pb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-[#B87333]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                    Hidden Costs & Rising Expenses
                  </h3>
                </div>
                <p className="text-slate-600 font-semibold leading-relaxed mb-4">
                  Small inefficiencies multiply—data re-entry, delayed
                  decisions, communication gaps silently erode margins. Expenses
                  consume{" "}
                  <strong className="text-[#B87333]">
                    68% of every revenue dollar
                  </strong>{" "}
                  due to talent costs, regulatory demands, and expansion
                  complexity.
                  <sup className="text-[#B87333] font-semibold ml-1">
                    <a href="#citation-1" className="hover:underline">
                      [1]
                    </a>
                  </sup>
                </p>
                <a
                  href="/case-studies"
                  className="inline-flex items-center text-[#B87333] font-semibold text-sm hover:gap-2 transition-all duration-200"
                >
                  See how we've solved for this
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>

              {/* Image Section Below */}
              <div className="relative h-64 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                <img
                  src="/hiddencosts.png"
                  alt="Hidden costs and rising expenses visualization"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* CARD 3: Scaling Complexity */}
            <div className="bg-white/90 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-slate-200 flex flex-col">
              {/* Text Section - White background at top */}
              <div className="bg-white/90 p-5 pb-4 sm:p-8 sm:pb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-[#B87333]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                    Scaling Complexity
                  </h3>
                </div>
                <p className="text-slate-600 font-semibold leading-relaxed mb-4">
                  More customers, more SKUs, more territories—
                  <strong className="text-[#B87333]">
                    complexity increases with scale
                  </strong>{" "}
                  but productivity doesn't keep up. Without the right systems,
                  your team drowns in spreadsheets instead of strategy.
                </p>
                <a
                  href="/case-studies"
                  className="inline-flex items-center text-[#B87333] font-semibold text-sm hover:gap-2 transition-all duration-200"
                >
                  See how we've solved for this
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>

              {/* Image Section Below */}
              <div className="relative h-64 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                <img
                  src="/scalingcomplexity.png"
                  alt="Scaling complexity visualization"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* CARD 4: Data Blindness */}
            <div className="bg-white/90 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-slate-200 flex flex-col">
              {/* Text Section - White background at top */}
              <div className="bg-white/90 p-5 pb-4 sm:p-8 sm:pb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#B87333]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-[#B87333]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                    Data Blindness
                  </h3>
                </div>
                <p className="text-slate-600 font-semibold leading-relaxed mb-4">
                  Without{" "}
                  <strong className="text-[#B87333]">
                    real-time, accurate data feedback loops
                  </strong>
                  , leadership flies blind—making strategic decisions on
                  outdated reports, gut instinct, and guesswork rather than
                  actionable insights that drive profitability.
                </p>
                <a
                  href="/case-studies"
                  className="inline-flex items-center text-[#B87333] font-semibold text-sm hover:gap-2 transition-all duration-200"
                >
                  See how we've solved for this
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>

              {/* Image Section Below */}
              <div className="relative h-64 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                <img
                  src="/data_blindness.png"
                  alt="Data blindness visualization"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>

        {/* THE BOTTOM LINE */}
        <div className="bg-white/90 rounded-2xl p-5 sm:p-8 lg:p-12 text-center shadow-xl border-2 border-slate-200">
          <p className="text-lg sm:text-xl lg:text-2xl text-slate-700 font-medium leading-relaxed max-w-[800px] mx-auto">
            "The business grows, but its efficiency and margins deteriorate.
            <span className="text-[#B87333] font-bold">
              {" "}
              Scale and asset growth no longer automatically translate into
              profitability."
            </span>
            <sup className="text-[#B87333] font-semibold ml-1">
              <a href="#citation-1" className="hover:underline">
                [1]
              </a>
            </sup>
          </p>
          <p className="text-slate-500 mt-6 text-sm">
            — PwC, "Asset and Wealth Management Revolution 2025"
          </p>
        </div>
      </div>
    </section>
  );
};

export default ResearchSection;
